import {
  SYMPTOM_ACUTE_SCREEN,
  SYMPTOM_QUESTIONS,
  SYMPTOM_SPECIFIC_SAFETY_V1,
  symptomLabel,
  type OlderSymptomRegistration,
  type RenewalSymptomAnswer,
  type StaffSymptomEntryView,
  type StaffSymptomUpdateView,
  type SymptomReviewRoute,
  type SymptomSafetyQuestion,
  type SymptomSourceSurface,
} from "@care-by-ella/contracts";

// TES-1252 (Ella 2.1 slice F) — her symptoms as the doctor reads them on the
// patient overview ("Besvär"). Pure functions so the wording is testable
// without rendering.
//
// Doctor only. A-01 ("Medicinsk informationsgräns"): "Medicinska
// strukturerade svar, … symptomdetaljer … ska inte visas som coachdata", and
// the route answers 403 to the coach and the admin (Ö-423's grant).
//
// The answer labels are her app's own (SYMPTOM_QUESTIONS, the safety
// questions), so the doctor reads what she chose. Everything else here is
// proposed staff copy.

/**
 * How a report she only saved to her history is labelled. Ö-675, Emelie
 * 2026-10-19, verbatim (TES-1440).
 */
export const PRIVATE_SYMPTOM_LABEL =
  "Registrerat av patienten – inte inskickat för granskning";
export const SUBMITTED_SYMPTOM_LABEL = "Inskickat för granskning";

/**
 * Shown once above the episodes when any private report is listed. Ö-447:
 * "Direktregistrering av besvär utanför en incheckning är inte en bevakad
 * vårdkanal."
 */
export const PRIVATE_SYMPTOM_NOTE =
  "Besvär som patienten registrerat utanför en incheckning är sparade i hennes historik. De är inte en bevakad vårdkanal och skapar inget ärende.";

export const OLDER_SYMPTOM_NOTE =
  "Samlade besvärssvar från incheckningar före den nya besvärsregistreringen, i den formulärversion hon svarade på. De gäller inte enskilda besvär och ingår inte i episoderna ovan.";

const SOURCE_LABELS: Record<SymptomSourceSurface, string> = {
  f02: "Hälsodeklaration (F-02)",
  f04: "Veckoincheckning (F-04)",
  f05: "Månadsincheckning (F-05)",
  f07: "Receptövertag (F-07)",
  renewal: "Förnyelse av recept",
  dose: "Vid dosregistrering",
  besvarsguide: "Direktregistrering i Besvärsguiden",
  health: "Direktregistrering i Din hälsa över tid",
};

export function symptomSourceLabel(source: string): string {
  return SOURCE_LABELS[source as SymptomSourceSurface] ?? source;
}

export function reviewRouteLabel(route: SymptomReviewRoute): string {
  return route === "submitted_for_review"
    ? SUBMITTED_SYMPTOM_LABEL
    : PRIVATE_SYMPTOM_LABEL;
}

function optionLabel(
  options: readonly { value: string; label: { sv: string } }[],
  value: string | null,
): string | null {
  if (value === null) return null;
  return options.find((o) => o.value === value)?.label.sv ?? value;
}

export const severityLabel = (v: string | null) =>
  optionLabel(SYMPTOM_QUESTIONS.SYMPTOM_SEVERITY.options, v);
export const trendLabel = (v: string | null) =>
  optionLabel(SYMPTOM_QUESTIONS.SYMPTOM_TREND.options, v);
export const intakeLabel = (v: string | null) =>
  optionLabel(SYMPTOM_QUESTIONS.SYMPTOM_INTAKE.options, v);

// Every safety question any version of the screen can have asked, by id.
const SAFETY_QUESTIONS = new Map<string, SymptomSafetyQuestion>(
  [
    SYMPTOM_ACUTE_SCREEN,
    ...Object.values(SYMPTOM_SPECIFIC_SAFETY_V1).flatMap((qs) => qs ?? []),
  ].map((q) => [q.id, q]),
);

/** "Question: answer, answer", one per safety question she answered. */
export function safetyAnswerLines(
  answers: StaffSymptomUpdateView["safetyAnswers"],
): string[] {
  if (!answers) return [];
  return Object.entries(answers).map(([id, raw]) => {
    const q = SAFETY_QUESTIONS.get(id);
    const values = Array.isArray(raw) ? raw : [raw];
    const labels = values.map((v) =>
      q ? (optionLabel(q.options, v) ?? v) : v,
    );
    return `${q ? q.label.sv : id} ${labels.join(", ")}`;
  });
}

const RULE_LABELS: Record<string, string> = {
  symptom_severe: "Kraftigt besvär",
  symptom_intake_often_hard: "Ofta svårt att äta och dricka",
};

/** The rule a report raised, in words. Urgent rules name the answer. */
export function safetyRuleLabel(rule: string): string {
  if (RULE_LABELS[rule]) return RULE_LABELS[rule];
  const urgent = /^symptom_urgent_(.+)$/.exec(rule);
  if (urgent) {
    for (const q of SAFETY_QUESTIONS.values()) {
      const o = q.options.find((opt) => opt.value === urgent[1]);
      if (o) return `Akut: ${o.label.sv}`;
    }
  }
  return rule;
}

/** What she was shown for this report, or null when nothing was raised. */
export function safetyLevelLine(
  u: Pick<StaffSymptomUpdateView, "safetyLevel" | "safetyRules">,
): string | null {
  if (u.safetyLevel === "none" && u.safetyRules.length === 0) return null;
  const head =
    u.safetyLevel === "acute"
      ? "Akut signal, patienten visades akutvägen"
      : u.safetyLevel === "severe"
        ? "Kraftig signal, patienten visades säkerhetstexten"
        : "Regel";
  const rules = u.safetyRules.map(safetyRuleLabel);
  return rules.length > 0 ? `${head}: ${rules.join(", ")}` : head;
}

export function formatSymptomInstant(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Europe/Stockholm",
  });
}

function formatDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

const KIND_LABELS: Record<StaffSymptomUpdateView["kind"], string> = {
  report: "Rapport",
  reopened: "Kommit tillbaka",
  resolved: "Borta, markerat av patienten",
};

/**
 * TES-1439 — a report about her earlier treatment (referencePeriod
 * before_ella): history with its context, never a current value.
 * Ö-793 (Emelie 2026-10-21): "Läkaretikett: ”Rapporterat från tidigare
 * behandling”. Förklaring: ”Historisk uppgift. Aktuell förekomst och
 * svårighetsgrad framgår endast om patienten har lämnat ett aktuellt
 * svar.” … Etiketten ska inte automatiskt fastställa att besväret är
 * avslutat eller inte ska följas upp." So neither line says "not current"
 * or "not followed up" any more.
 */
export const BEFORE_ELLA_REPORT_LABEL = "Rapporterat från tidigare behandling";
export const HISTORY_ENTRY_STATUS = BEFORE_ELLA_REPORT_LABEL;
export const HISTORY_ENTRY_EXPLANATION =
  "Historisk uppgift. Aktuell förekomst och svårighetsgrad framgår endast om patienten har lämnat ett aktuellt svar.";

/** The one-line summary of a report: what she answered. */
export function symptomUpdateSummary(u: StaffSymptomUpdateView): string {
  const parts = [
    u.referencePeriod === "before_ella"
      ? BEFORE_ELLA_REPORT_LABEL
      : (KIND_LABELS[u.kind] ?? u.kind),
  ];
  if (u.kind === "resolved") return parts[0];
  const severity = severityLabel(u.severity);
  if (severity) parts.push(severity);
  const trend = trendLabel(u.trend);
  // "Kommit tillbaka" is already the kind of a reopening report.
  if (trend && !(u.kind === "reopened" && u.trend === "kommit_tillbaka")) {
    parts.push(trend);
  }
  const intake = intakeLabel(u.intake);
  if (intake) parts.push(`Äta och dricka: ${intake}`);
  if (u.startedOn) {
    parts.push(
      `Började ${u.startedOnApprox ? "cirka " : ""}${formatDay(u.startedOn)}`,
    );
  }
  return parts.join(" · ");
}

/** Where the report came from and whether it was submitted. */
export function symptomUpdateProvenance(u: StaffSymptomUpdateView): string {
  const parts = [
    formatSymptomInstant(u.observedAt),
    symptomSourceLabel(u.sourceSurface),
  ];
  if (u.doseEventId) parts.push("kopplad till en dosregistrering");
  parts.push(reviewRouteLabel(u.reviewRoute));
  return parts.join(" · ");
}

/** The episode's heading line: symptom, status and where it began. */
export function symptomEntryHeading(e: StaffSymptomEntryView): {
  title: string;
  status: string;
  meta: string;
} {
  const latest = e.latestReport;
  const now = latest
    ? [severityLabel(latest.severity), trendLabel(latest.trend)]
        .filter(Boolean)
        .join(", ")
    : "";
  return {
    title: e.label?.sv ?? e.symptomCode,
    status:
      e.status === "resolved"
        ? `Borta${e.resolvedAt ? ` sedan ${formatSymptomInstant(e.resolvedAt)}` : ""}`
        : e.status === "history"
          ? HISTORY_ENTRY_STATUS
          : "Pågående",
    meta: [
      now ? `Senast: ${now}` : null,
      `Först registrerat ${formatSymptomInstant(e.firstReportedAt)}`,
      symptomSourceLabel(e.createdFrom),
    ]
      .filter(Boolean)
      .join(" · "),
  };
}

/** True when any listed report was only saved to her own history. */
export function hasPrivateSymptomReports(
  entries: readonly StaffSymptomEntryView[],
): boolean {
  return entries.some((e) =>
    e.updates.some(
      (u) => u.kind !== "resolved" && u.reviewRoute === "private_history",
    ),
  );
}

/**
 * TES-1443 — one register symptom she chose in the renewal check-in, on the
 * renewal panel: the symptom, her answers as an episode lists them, and what
 * she was shown when that was more than nothing. Her note is printed apart,
 * in her own words.
 */
export function renewalSymptomLine(s: RenewalSymptomAnswer): string {
  const parts = [symptomLabel(s.symptomCode)?.sv ?? s.symptomCode];
  const severity = severityLabel(s.severity);
  if (severity) parts.push(severity);
  const trend = trendLabel(s.trend);
  if (trend) parts.push(trend);
  const intake = intakeLabel(s.intake);
  if (intake) parts.push(`Äta och dricka: ${intake}`);
  if (s.startedOn) parts.push(`Började ${formatDay(s.startedOn)}`);
  const safety = safetyLevelLine({
    safetyLevel: s.safetyLevel as StaffSymptomUpdateView["safetyLevel"],
    safetyRules: [],
  });
  if (safety) parts.push(safety);
  return parts.join(" · ");
}

/** "Hur har veckan varit? · 1 sep. 2026 · version 4". */
export function olderRegistrationHeading(r: OlderSymptomRegistration): string {
  return [
    r.sourceTitle.sv,
    formatSymptomInstant(r.submittedAt),
    `version ${r.templateVersion}`,
  ].join(" · ");
}
